import * as Linking from 'expo-linking';
import { Link, useRouter } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { Platform, Pressable, View } from 'react-native';

import { Button } from '@/components/ui/Button';
import { Form } from '@/components/ui/Form';
import { PasswordField } from '@/components/ui/PasswordField';
import { StatusBanner } from '@/components/ui/StatusBanner';
import { Text } from '@/components/ui/Text';
import { useAuth } from '@/lib/auth';
import { MIN_PASSWORD_LENGTH, PASSWORD_PLACEHOLDER } from '@/lib/constants';
import {
  consumeRecoverySession,
  getRecoverySnapshot,
  markRecoverySession,
  useConsumeRecoveryOnUnmount,
  useRecoverySession,
} from '@/lib/recovery';
import { supabase } from '@/lib/supabase';
import { useAsyncAction } from '@/lib/useAsyncAction';

/**
 * Set a new password after a password-reset email.
 *
 * The password change is gated on a genuinely-established recovery session for the
 * account being changed, NOT on password length alone. Without that gate the screen
 * changes the account password on whatever session the client happens to hold — and
 * because it lives outside the `(app)` auth gate, a merely-signed-in user (an unlocked
 * device) reaches it and bypasses the current-password reauth the account screen
 * enforces. The recovery mark comes from `@/lib/recovery`, set by the
 * `PASSWORD_RECOVERY` listener in `supabase.ts`; see that module for why it can't be
 * observed from here, and why it records a user id rather than a bare flag.
 */
export function NewPasswordForm() {
  const router = useRouter();
  const { session, status } = useAuth();
  const recoveringUserId = useRecoverySession();
  const [password, setPassword] = useState('');
  const { busy, error, setError, run } = useAsyncAction();
  // A recovery deep link is single-use: guard against re-handling it if
  // Linking.useURL() re-emits the same URL (relaunch/refocus). Keyed by the handled
  // url rather than a boolean — a DIFFERENT link is a NEW attempt: after an expired
  // link, the screen itself sends the user off for a fresh email, and that fresh link
  // deep-links back into this same, still-mounted screen. A boolean would swallow it
  // and leave the stale "expired" error as the end of the road until a force-quit.
  const handled = useRef<string | null>(null);
  const url = Linking.useURL();
  // A failed session hand-off (the deep link's tokens are still good, the network
  // wasn't) surfaces a "Try again" that reopens `handled` and bumps this nonce to
  // re-run the deep-link effect. Re-tapping the email link cannot do that: on the
  // second tap Linking.useURL() returns the identical string, React bails out on the
  // no-op state update, and the effect never re-runs in this app session.
  const [retryNonce, setRetryNonce] = useState(0);
  const [canRetryHandOff, setCanRetryHandOff] = useState(false);

  // The reset link reports its outcome in the URL FRAGMENT — tokens when it works, an
  // error when it is expired or already used. (This client never sets `flowType`, so it
  // is on the implicit flow; there is no PKCE `?code` to exchange.)
  //
  // Web: supabase-js's `detectSessionInUrl` has already consumed a GOOD fragment before
  // this screen mounted — it fires PASSWORD_RECOVERY, then clears the hash. It does NOT
  // clear a BAD one: `_initialize` swallows that error and returns, leaving the fragment
  // in place. So a fragment still readable here on web is an error, and this is the only
  // place left to read it; without this the user sees nothing but the caution banner and
  // never learns their link expired.
  //
  // Native: there is no document to parse, the link arrives as a deep link, and nothing
  // has touched it. Establish the session from its tokens, then mark recovery.
  useEffect(() => {
    // What we key single-use handling on: the deep-link url on native, the location
    // hash on web (there may be no deep-link url there at all).
    const source = Platform.OS === 'web' ? window.location.hash : (url ?? '');
    if (!source || handled.current === source) return;
    const hash =
      Platform.OS === 'web' ? source.replace(/^#/, '') : (source.split('#')[1] ?? '');
    if (!hash) return;
    const params = new URLSearchParams(hash);
    const errorDescription = params.get('error_description');
    if (errorDescription) {
      handled.current = source;
      setError(errorDescription);
      return;
    }
    // Everything past here is the native session hand-off. On web a token-bearing
    // fragment means supabase-js simply hasn't finished with it yet — it owns that
    // exchange, and PASSWORD_RECOVERY will land on its own.
    if (Platform.OS === 'web') return;
    if (params.get('type') !== 'recovery') return;
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    if (!accessToken || !refreshToken) return;
    handled.current = source;
    void (async () => {
      const { data, error: e } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
      if (e) {
        // The common failure here is OFFLINE, and the tokens in the fragment are
        // still good — offer a retry instead of burning a valid link on a dropped
        // connection and forcing a whole new email.
        setCanRetryHandOff(true);
        setError(e.message);
      } else if (data.session) {
        setCanRetryHandOff(false);
        // A stale error from an earlier link (expired, offline) must not sit over a
        // session that just got established.
        setError(null);
        markRecoverySession(data.session.user.id);
      }
    })();
  }, [url, retryNonce, setError]);

  // The mark authorizes a password change on THIS screen, so it dies with this screen. Left
  // standing, an abandoned recovery (link followed, password never set, screen navigated away
  // from) arms the route for the rest of the app's life: anyone who reaches it on that device
  // afterwards changes the password with no reauth. On the success path save() has already
  // consumed it before router.replace, so this is a no-op there.
  useConsumeRecoveryOnUnmount();

  // setSession/PASSWORD_RECOVERY established a recovery session, and it is a recovery
  // session for the account we are about to change. The id comparison is what stops a
  // mark left over from an earlier, abandoned recovery from authorizing a change on
  // whoever is signed in now.
  const recovered = recoveringUserId !== null && recoveringUserId === session?.user.id;
  const canSave = recovered && password.length >= MIN_PASSWORD_LENGTH;
  // `recovered` reads the session, which is null until the persisted-session load lands — so
  // until it does, a perfectly good recovery link is indistinguishable from no link at all.
  // Say nothing rather than flash "open this from your reset email" at someone who just did.
  // A FAILED load ('error') is different: it never resolves on its own, and this route sits
  // outside the (app) group that SessionErrorScreen covers — withholding the help there
  // would leave a permanently disabled Save with nothing to press.
  const showNoRecoveryHelp = (status === 'ready' || status === 'error') && !recovered;

  async function save() {
    await run(async () => {
      // The authoritative gate — the disabled button is only cosmetic. Refuse to
      // change the password unless THIS session came from the recovery flow, so an
      // ambient session can never reach updateUser.
      if (!session || getRecoverySnapshot() !== session.user.id) {
        throw new Error('Open this screen from your password reset email to continue.');
      }
      const { error: e } = await supabase.auth.updateUser({ password });
      if (e) throw e;
      // Single-use: a completed change must not authorize another later in the
      // same app session.
      consumeRecoverySession();
      router.replace('/');
    });
  }

  return (
    <Form
      onSubmit={() => {
        if (canSave) save();
      }}
      className="gap-5"
    >
      <Text variant="subtitle">
        Choose a new password — you&apos;ll stay signed in on this device.
      </Text>

      <PasswordField
        label="New password"
        value={password}
        onChangeText={setPassword}
        placeholder={PASSWORD_PLACEHOLDER}
        autoComplete="new-password"
        textContentType="newPassword"
        helper={`Use ${MIN_PASSWORD_LENGTH} or more characters.`}
        showStrength
      />

      {error ? (
        <Text variant="caption" className="text-alert">
          {error}
        </Text>
      ) : null}

      {error && canRetryHandOff ? (
        <Pressable
          hitSlop={8}
          className="self-center"
          onPress={() => {
            handled.current = null;
            setError(null);
            setRetryNonce((n) => n + 1);
          }}
        >
          <Text variant="caption" className="text-signal">
            Try again
          </Text>
        </Pressable>
      ) : null}

      <Button block submit label="Save password" busy={busy} disabled={!canSave} />

      {showNoRecoveryHelp ? (
        <>
          <StatusBanner
            variant="caution"
            title="Open this from your reset email"
            subtitle="The secure link establishes your recovery session."
          />
          {/* Both ways out of a screen that can otherwise only be reached, never left: the
              link is expired or was never followed (ask for a fresh one), or the person is
              signed in already and wants the reauthenticated change instead. */}
          <View className="gap-3">
            <Link href="/reset/request" asChild>
              <Pressable hitSlop={8} className="self-center">
                <Text variant="caption" className="text-signal">
                  Send a new reset email
                </Text>
              </Pressable>
            </Link>
            {session ? (
              <Link href="/settings/account" asChild>
                <Pressable hitSlop={8} className="self-center">
                  <Text variant="caption" className="text-signal">
                    Signed in? Change your password in Account settings
                  </Text>
                </Pressable>
              </Link>
            ) : null}
          </View>
        </>
      ) : null}
    </Form>
  );
}
